import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { User } from '@/features/users/services/users.service';
import { getMentionDisplayName } from '@/features/daily-tasks/utils/mention-utils';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string, notifyUserIds: string[]) => void;
  users: User[];
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
}

export function MentionTextarea({
  value,
  onChange,
  users,
  placeholder = 'Escreva um comentário (use @ para mencionar)',
  rows = 3,
  className,
  disabled = false,
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!value) {
      setNotifyUserIds([]);
    }
  }, [value]);

  const filteredUsers =
    mentionQuery !== null
      ? users
          .filter((user) => {
            const mentionName = getMentionDisplayName(user).toLowerCase();
            const email = user.email.toLowerCase();
            const fullName = user.fullName?.toLowerCase() ?? '';
            const query = mentionQuery.toLowerCase();
            return (
              mentionName.includes(query) ||
              email.includes(query) ||
              fullName.includes(query)
            );
          })
          .slice(0, 8)
      : [];

  const syncNotifyUserIds = useCallback(
    (text: string, currentIds: string[]) => {
      const validIds = currentIds.filter((id) => {
        const matchedUser = users.find((candidate) => candidate.id === id);
        if (!matchedUser) return false;
        return text.includes(`@${getMentionDisplayName(matchedUser)}`);
      });
      return validIds;
    },
    [users],
  );

  const detectMention = useCallback((text: string, cursorPos: number) => {
    const beforeCursor = text.slice(0, cursorPos);
    const match = beforeCursor.match(/@([^\s@]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionStart(cursorPos - match[0].length);
      setSelectedIndex(0);
      return;
    }
    setMentionQuery(null);
    setMentionStart(null);
  }, []);

  const emitChange = useCallback(
    (text: string, ids: string[]) => {
      const validIds = syncNotifyUserIds(text, ids);
      setNotifyUserIds(validIds);
      onChange(text, validIds);
    },
    [onChange, syncNotifyUserIds],
  );

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    const cursor = event.target.selectionStart ?? text.length;
    detectMention(text, cursor);
    emitChange(text, notifyUserIds);
  };

  const insertMention = (selectedUser: User) => {
    if (mentionStart === null || !textareaRef.current) return;

    const displayName = getMentionDisplayName(selectedUser);
    const cursor = textareaRef.current.selectionStart ?? value.length;
    const before = value.slice(0, mentionStart);
    const after = value.slice(cursor);
    const newText = `${before}@${displayName} ${after}`;
    const newIds = notifyUserIds.includes(selectedUser.id)
      ? notifyUserIds
      : [...notifyUserIds, selectedUser.id];

    emitChange(newText, newIds);
    setMentionQuery(null);
    setMentionStart(null);

    requestAnimationFrame(() => {
      const nextCursor = before.length + displayName.length + 2;
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
      textareaRef.current?.focus();
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || filteredUsers.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((index) => Math.min(index + 1, filteredUsers.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      insertMention(filteredUsers[selectedIndex]);
    } else if (event.key === 'Escape') {
      setMentionQuery(null);
      setMentionStart(null);
    }
  };

  const handleSelect = () => {
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    detectMention(value, cursor);
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleSelect}
        onKeyUp={handleSelect}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={cn('resize-none', className)}
      />

      {mentionQuery !== null && filteredUsers.length > 0 && (
        <div
          className="absolute bottom-full left-0 right-0 z-[10060] mb-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md"
          role="listbox"
          aria-label="Usuários para mencionar"
        >
          {filteredUsers.map((candidate, index) => (
            <button
              key={candidate.id}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                index === selectedIndex && 'bg-muted',
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                insertMention(candidate);
              }}
            >
              <span className="font-medium">{getMentionDisplayName(candidate)}</span>
              <span className="truncate text-xs text-muted-foreground">
                {candidate.fullName ?? candidate.email}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
